/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: rating-overlay-asset-service.ts
 *
 * Created by kyang on 10/21/20.
 */

// This service is an implementation for BrandingAssetService.
// Components should invoke the abstract BrandingAssetService, not this specific implementation.
//
// BrandingAssetService implementations work on brandingAssetContainers.
// However, rating overlays don't have a container.
// So, use the list of overlays as the brandingAssetContainer instead.

angular.module('prism-ui.components.branding').service('RatingOverlayAssetService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    NetworkResourceFactory: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingViewService: JS5Object,
) {

    const RATING_OVERLAY_TYPE = 'ratingOverlays';

    //==========================//
    // Branding Asset Container //
    //==========================//

    const createRatingOverlayBrandingContainer = (
        networkId: string,
        ratingOverlays: JS5Object[],
    ) => {
        return {
            id: networkId,
            assets: ratingOverlays
        };
    };

    this.getBrandingAssetContainer = (networkId: string) => {
        return NetworkResourceFactory.getBranding({id: networkId, view: RATING_OVERLAY_TYPE}).$promise
            .then((ratingOverlays: JS5Object[]) => {
                return createRatingOverlayBrandingContainer(networkId, ratingOverlays);
            });
    };

    this.parseBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        brandingAssetContainer.assets = BrandingAssetService.filterBrandingAssets(brandingConfig, brandingAssetContainer.assets, {anySeasonVolume: true});
    };

    this.updateBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        return $q.reject({
            description: 'Rating overlays use a mock brandingAssetContainer, which cannot be saved to the database'
        });
    };


    //=======//
    // Asset //
    //=======//

    const parseRatingOverlayDTO = (ratingOverlay: JS5Object) => {
        _.forEach(ratingOverlay.images, (image: JS5Object) => {
            image.mediaAcceptProfile = ratingOverlay.mediaAcceptProfile;
            if (_.isEmpty(image.ratingDescriptors)) {
                image.ratingDescriptors = [{
                    id: -1,
                    name: '[None]'
                }];
            }
        });
        return ratingOverlay;
    };

    const createEmptyRootBrandingAsset = (brandingConfig: JS5Object, acceptProfile: JS5Object) => {
        let rootAsset: JS5Object = {
            mediaAcceptProfile: acceptProfile,
            images: [],
        };
        return rootAsset;
    };

    this.getRootBrandingAsset = (brandingConfig: JS5Object, container: JS5Object, acceptProfile: JS5Object) => {
        let ratingOverlays = BrandingAssetService.filterBrandingAssets(brandingConfig, container.assets)
            .map(parseRatingOverlayDTO);
        let rootAsset = _.find(ratingOverlays, {
            mediaAcceptProfile: {id: acceptProfile.id}
        });
        return (rootAsset) ? rootAsset : createEmptyRootBrandingAsset(brandingConfig, acceptProfile);
    };

    this.validateNonTextAsset = (acceptProfileId: number, asset: JS5Object, virtualPath: string) => {
        // Rating overlay images aren't validated
        return $q.when([]);
    };

    const prepareRatingOverlay = (ratingOverlay: JS5Object) => {
        _.forEach(ratingOverlay.images, (image: JS5Object) => {
            _.unset(image, 'mediaAcceptProfile');
            _.remove(image.ratingDescriptors, {id: -1});
        });
    };

    this.createRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        prepareRatingOverlay(rootAsset);
        return NetworkResourceFactory.saveBranding({id: brandingConfig.id, view: RATING_OVERLAY_TYPE}, rootAsset).$promise
            .then(parseRatingOverlayDTO);
    };

    this.updateRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        prepareRatingOverlay(rootAsset);
        return NetworkResourceFactory.saveBranding({id: brandingConfig.id, view: RATING_OVERLAY_TYPE}, rootAsset).$promise
            .then(parseRatingOverlayDTO);
    };


    //=========//
    // Proxies //
    //=========//

    this.hasVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        return _.some(rootAsset.images, (image: JS5Object) => {
            return !BrandingAssetService.assetWasIngested(image);
        });
    };

    const getRatingOverlayImageProxyStatus = (updatedImage: JS5Object) => {
        if (BrandingAssetService.assetHasProxy(updatedImage)) {
            return BrandingAssetService.ProxyStatus.PROXY_COMPLETE;
        } else if (_.get(updatedImage, 'ingestInfo.workflowStatus') === BrandingAssetService.ProxyStatus.PROXY_STALLED) {
            return BrandingAssetService.ProxyStatus.PROXY_STALLED;
        }
        return BrandingAssetService.ProxyStatus.PROXY_RUNNING;
    };

    this.updateVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object, intervalData: JS5Object) => {
        return this.getBrandingAssetContainer(brandingConfig.id)
            .then((updatedContainer: JS5Object) => {
                let updatedAsset = this.getRootBrandingAsset(brandingConfig, updatedContainer, rootAsset.mediaAcceptProfile);
                let finalState = BrandingAssetService.ProxyStatus.PROXY_COMPLETE;
                _.forEach(updatedAsset.images, (image: JS5Object) => {
                    let updatedStatus = getRatingOverlayImageProxyStatus(image);
                    let prevStatus = _.get(intervalData, image.id, updatedStatus);
                    if (updatedStatus !== prevStatus) {
                        BrandingViewService.showVideoIngestStatusNotifications(brandingConfig, image, updatedStatus, `- ${image.rating.name} `);
                    }
                    switch (updatedStatus) {
                        case BrandingAssetService.ProxyStatus.PROXY_COMPLETE:
                            break;
                        case BrandingAssetService.ProxyStatus.PROXY_STALLED:
                            if (finalState === BrandingAssetService.ProxyStatus.PROXY_COMPLETE) {
                                finalState = BrandingAssetService.ProxyStatus.PROXY_STALLED;
                            }
                            break;
                        case BrandingAssetService.ProxyStatus.PROXY_RUNNING:
                            finalState = BrandingAssetService.ProxyStatus.PROXY_RUNNING;
                    }
                    intervalData[image.id] = updatedStatus;
                });

                return finalState;
            });
    };
});


//=================//
// Lifecycle Hooks //
//=================//

// Load this service when app is initialized
angular.module('prism-ui.components.branding').run((
    BrandingAssetService: JS5Object,
    RatingOverlayAssetService: JS5Object,
) => {
    BrandingAssetService.registerAssetServiceImplementation('ratingOverlays', RatingOverlayAssetService);
});
